import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { ClientGroupDetail, ClientGroupManageRequest } from '../../types/adminClientGroup';

type Mode = 'create' | 'edit';

interface ClientGroupFormDialogProps {
  open: boolean;
  mode: Mode;
  group?: ClientGroupDetail | null;
  saving: boolean;
  onClose: () => void;
  onCreate: (payload: ClientGroupManageRequest) => void;
  onUpdate: (id: number, payload: ClientGroupManageRequest) => void;
}

const ClientGroupFormDialog: React.FC<ClientGroupFormDialogProps> = ({
  open,
  mode,
  group,
  saving,
  onClose,
  onCreate,
  onUpdate,
}) => {
  const [name, setName] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [requiresOAuth, setRequiresOAuth] = useState(true);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setValidationError(null);
    if (mode === 'edit' && group) {
      setName(group.name);
      setClientId(group.clientId);
      setClientSecret(group.clientSecret || '');
      setRequiresOAuth(group.requiresOAuth);
    } else {
      setName('');
      setClientId('');
      setClientSecret('');
      setRequiresOAuth(true);
    }
  }, [open, mode, group]);

  const handleSubmit = () => {
    setValidationError(null);

    if (!name.trim()) {
      setValidationError('Name is required');
      return;
    }
    if (!clientId.trim()) {
      setValidationError('Client ID is required');
      return;
    }
    if (mode === 'create' && !clientSecret.trim()) {
      setValidationError('Client secret is required');
      return;
    }

    const payload: ClientGroupManageRequest = {
      name: name.trim(),
      clientId: clientId.trim(),
      requiresOAuth,
    };
    if (clientSecret.trim()) {
      payload.clientSecret = clientSecret.trim();
    }

    if (mode === 'create') {
      onCreate(payload);
    } else if (group) {
      onUpdate(group.id, payload);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === 'create' ? 'Create Client Group' : `Edit Client Group — ${group?.name}`}</DialogTitle>
      <DialogContent dividers>
        {validationError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {validationError}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gap: 2 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            required
            fullWidth
          />
          <TextField
            label="Client ID"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            disabled={saving}
            required
            fullWidth
            helperText={
              mode === 'edit'
                ? 'Changing the Client ID forces affected devices to re-fetch their OAuth tokens'
                : 'Used by devices to obtain OAuth tokens'
            }
          />
          <TextField
            label="Client Secret"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            disabled={saving}
            required={mode === 'create'}
            fullWidth
            type="text"
            helperText={mode === 'edit' ? 'Edit to set a new secret, or clear to keep the current one' : undefined}
          />
          <FormControlLabel
            control={
              <Switch
                checked={requiresOAuth}
                onChange={(e) => setRequiresOAuth(e.target.checked)}
                disabled={saving}
              />
            }
            label="Requires OAuth (v2)"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} /> : <Save />}
        >
          {mode === 'create' ? 'Create' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientGroupFormDialog;
